import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Switch,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useGetSettingsQuery, useUpdateSettingsMutation } from 'state/api';
import { useSnack, SaveSnackbar } from './settingsHelper';

const CustomerSettings = () => {
  const { data } = useGetSettingsQuery();
  const settings = data?.data || {};
  const [updateSettings, { isLoading }] = useUpdateSettingsMutation();
  const { snack, showSuccess, showError, closeSnack } = useSnack();

  const [addRefundAmountToWallet, setAddRefundAmountToWallet] = useState(false);
  const [addFundToWallet, setAddFundToWallet] = useState(false);
  const [minimumAddFundAmount, setMinimumAddFundAmount] = useState(100);
  const [maximumAddFundAmount, setMaximumAddFundAmount] = useState(1000);
  const [customerLoyaltyPoint, setCustomerLoyaltyPoint] = useState(false);
  const [equivalentPointTo1UnitCurrency, setEquivalentPointTo1UnitCurrency] = useState(10);
  const [loyaltyPointEarnOnEachOrder, setLoyaltyPointEarnOnEachOrder] = useState(5);
  const [minimumPointRequiredToConvert, setMinimumPointRequiredToConvert] = useState(200);
  const [customerReferralEarning, setCustomerReferralEarning] = useState(false);
  const [earningToEachReferral, setEarningToEachReferral] = useState(3);

  useEffect(() => {
    const c = settings.custom?.customerSettings;
    if (!c) return;
    if (c.addRefundAmountToWallet !== undefined) setAddRefundAmountToWallet(c.addRefundAmountToWallet);
    if (c.addFundToWallet !== undefined) setAddFundToWallet(c.addFundToWallet);
    if (c.minimumAddFundAmount !== undefined) setMinimumAddFundAmount(c.minimumAddFundAmount);
    if (c.maximumAddFundAmount !== undefined) setMaximumAddFundAmount(c.maximumAddFundAmount);
    if (c.customerLoyaltyPoint !== undefined) setCustomerLoyaltyPoint(c.customerLoyaltyPoint);
    if (c.equivalentPointTo1UnitCurrency !== undefined) setEquivalentPointTo1UnitCurrency(c.equivalentPointTo1UnitCurrency);
    if (c.loyaltyPointEarnOnEachOrder !== undefined) setLoyaltyPointEarnOnEachOrder(c.loyaltyPointEarnOnEachOrder);
    if (c.minimumPointRequiredToConvert !== undefined) setMinimumPointRequiredToConvert(c.minimumPointRequiredToConvert);
    if (c.customerReferralEarning !== undefined) setCustomerReferralEarning(c.customerReferralEarning);
    if (c.earningToEachReferral !== undefined) setEarningToEachReferral(c.earningToEachReferral);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleAddRefundAmountToWalletChange = (event) => setAddRefundAmountToWallet(event.target.checked);
  const handleAddFundToWalletChange = (event) => setAddFundToWallet(event.target.checked);
  const handleMinimumAddFundAmountChange = (event) => setMinimumAddFundAmount(event.target.value);
  const handleMaximumAddFundAmountChange = (event) => setMaximumAddFundAmount(event.target.value);
  const handleEquivalentPointTo1UnitCurrencyChange = (event) => setEquivalentPointTo1UnitCurrency(event.target.value);
  const handleLoyaltyPointEarnOnEachOrderChange = (event) => setLoyaltyPointEarnOnEachOrder(event.target.value);
  const handleMinimumPointRequiredToConvertChange = (event) => setMinimumPointRequiredToConvert(event.target.value);
  const handleEarningToEachReferralChange = (event) => setEarningToEachReferral(event.target.value);

  const handleSave = async () => {
    try {
      await updateSettings({
        custom: {
          customerSettings: {
            addRefundAmountToWallet,
            addFundToWallet,
            minimumAddFundAmount,
            maximumAddFundAmount,
            customerLoyaltyPoint,
            equivalentPointTo1UnitCurrency,
            loyaltyPointEarnOnEachOrder,
            minimumPointRequiredToConvert,
            customerReferralEarning,
            earningToEachReferral,
          },
        },
      }).unwrap();
      showSuccess();
    } catch (err) {
      showError(err?.data?.error?.message);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Accordion disableGutters sx={{ bgcolor: 'background.paper' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography variant="h6" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <ShoppingCartIcon sx={{ mr: 1, color: 'primary.main' }} /> Customer Wallet
                  <InfoIcon color="primary" />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" color="text.primary">
                      Add Refund Amount to Wallet
                      <InfoIcon color="primary" />
                    </Typography>
                    <Switch checked={addRefundAmountToWallet} onChange={handleAddRefundAmountToWalletChange} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" color="text.primary">
                      Add Fund to Wallet
                      <InfoIcon color="primary" />
                    </Typography>
                    <Switch checked={addFundToWallet} onChange={handleAddFundToWalletChange} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Minimum Add Fund Amount (USD)"
                      type="number"
                      value={minimumAddFundAmount}
                      onChange={handleMinimumAddFundAmountChange}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Maximum Add Fund Amount (USD)"
                      type="number"
                      value={maximumAddFundAmount}
                      onChange={handleMaximumAddFundAmountChange}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Accordion disableGutters sx={{ bgcolor: 'background.paper' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2a-content"
                id="panel2a-header"
              >
                <Typography variant="h6" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <ShoppingCartIcon sx={{ mr: 1, color: 'primary.main' }} /> Customer Loyalty Point
                  <InfoIcon color="primary" />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Equivalent Point To 1 Unit Currency"
                      type="number"
                      value={equivalentPointTo1UnitCurrency}
                      onChange={handleEquivalentPointTo1UnitCurrencyChange}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Loyalty Point Earn On Each Order(%)"
                      type="number"
                      value={loyaltyPointEarnOnEachOrder}
                      onChange={handleLoyaltyPointEarnOnEachOrderChange}
                      InputProps={{ inputProps: { min: 0, max: 100 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Minimum Point Required To Convert"
                      type="number"
                      value={minimumPointRequiredToConvert}
                      onChange={handleMinimumPointRequiredToConvertChange}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Accordion disableGutters sx={{ bgcolor: 'background.paper' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel3a-content"
                id="panel3a-header"
              >
                <Typography variant="h6" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <ShoppingCartIcon sx={{ mr: 1, color: 'primary.main' }} /> Customer Referral Earning
                  <InfoIcon color="primary" />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <TextField
                      fullWidth
                      label="Earning To Each Referral (USD)"
                      type="number"
                      value={earningToEachReferral}
                      onChange={handleEarningToEachReferralChange}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" color="primary" fullWidth onClick={handleSave} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </Grid>
        </Grid>

        <SaveSnackbar snack={snack} onClose={closeSnack} />
      </CardContent>
    </Card>
  );
};

export default CustomerSettings;
